import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Barcode, MagnifyingGlass, X, CloudArrowDown, Percent, ArrowsClockwise } from "@phosphor-icons/react";
import BarcodeScanner from "../components/BarcodeScanner";
import { useAuth } from "../lib/auth";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);

const empty = { name: "", barcode: "", category: "Repuestos", description: "", price_buy: 0, price_sell: 0, stock: 0, stock_min: 5, supplier_id: "" };

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [importOpen, setImportOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ done: 0, total: 0 });
  const [syncResult, setSyncResult] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjust, setAdjust] = useState({ percentage: 10, direction: "increase", target: "sale", category: "Todos", round_to: 0 });
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLimit, setImportLimit] = useState("");
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/products"),
      isAdmin ? api.get("/suppliers") : Promise.resolve({ data: [] }),
    ]);
    setProducts(p.data);
    setSuppliers(s.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = products.filter((p) => {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const haystack = [
      p.name,
      p.category,
      p.barcode,
      p.description,
      p.supplier_name,
    ].filter(Boolean).join(" ").toLowerCase();
    return tokens.every((t) => haystack.includes(t));
  });

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => { setEditing(p.product_id); setForm({ ...p, supplier_id: p.supplier_id || "" }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_buy: parseFloat(form.price_buy) || 0,
      price_sell: parseFloat(form.price_sell) || 0,
      stock: parseInt(form.stock) || 0,
      stock_min: parseInt(form.stock_min) || 0,
      supplier_id: form.supplier_id || null,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar producto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Eliminado");
    load();
  };

  const runSyncPrices = async ({ dryRun = false } = {}) => {
    setSyncing(true);
    setSyncResult(null);
    setSyncProgress({ done: 0, total: 0 });
    const BATCH = 150;
    let offset = 0;
    let totalAll = 0;
    let updated = 0, unchanged = 0, notFound = 0, errors = 0;
    let details = [];
    try {
      while (true) {
        const params = new URLSearchParams({
          offset: String(offset),
          batch_size: String(BATCH),
          dry_run: dryRun ? "true" : "false",
        });
        const { data } = await api.post(
          `/products/sync-prices/criye?${params.toString()}`,
          {},
          { timeout: 90000 },
        );
        totalAll = data.total_urls || 0;
        updated += data.updated || 0;
        unchanged += data.unchanged || 0;
        notFound += data.not_found || 0;
        errors += data.errors || 0;
        details = details.concat(data.details || []);
        const end = (data.processed_range && data.processed_range[1]) || offset + BATCH;
        setSyncProgress({ done: end, total: totalAll });
        if (!data.next_offset) break;
        offset = data.next_offset;
      }
      setSyncResult({ dryRun, updated, unchanged, notFound, errors, details, total_urls: totalAll });
      toast.success(
        dryRun
          ? `Vista previa: ${updated} precios cambiarían`
          : `Sincronización OK: ${updated} precios actualizados, ${unchanged} sin cambio`
      );
      if (!dryRun) load();
    } catch (e) {
      setSyncResult({ dryRun, updated, unchanged, notFound, errors, details, total_urls: totalAll, partial: true });
      toast.error(e.response?.data?.detail || `Error (parcial: ${updated} precios actualizados)`);
    } finally {
      setSyncing(false);
    }
  };


  const runImport = async () => {
    setImporting(true);
    setImportResult(null);
    setImportProgress({ done: 0, total: 0 });
    const lim = parseInt(importLimit) || 0;
    const BATCH = 150;
    let offset = 0;
    let totalAll = 0;
    let created = 0;
    let skipped = 0;
    let errors = 0;
    let details = [];
    try {
      // Loop through batches until backend signals no more URLs to process
      // Each batch is small enough to finish before Cloudflare's ~100s timeout
      while (true) {
        const params = new URLSearchParams();
        if (lim > 0) params.set("limit", String(lim));
        params.set("offset", String(offset));
        params.set("batch_size", String(BATCH));
        const { data } = await api.post(
          `/products/import/criye?${params.toString()}`,
          {},
          { timeout: 90000 },
        );
        totalAll = data.total_urls || 0;
        created += data.created || 0;
        skipped += data.skipped || 0;
        errors += data.errors || 0;
        details = details.concat(data.details || []);
        const processedEnd = (data.processed_range && data.processed_range[1]) || offset + BATCH;
        setImportProgress({ done: processedEnd, total: totalAll });
        if (!data.next_offset) break;
        offset = data.next_offset;
      }
      setImportResult({ created, skipped, errors, details, total_urls: totalAll });
      toast.success(`Importación completa: ${created} nuevos, ${skipped} duplicados saltados`);
      load();
    } catch (e) {
      setImportResult({ created, skipped, errors, details, total_urls: totalAll, partial: true });
      toast.error(
        e.response?.data?.detail ||
        `Error en la importación (parcial: ${created} cargados antes del corte)`,
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="inventory-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Stock</p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Inventario</h1>
          <p className="text-zinc-400 mt-1 text-sm">{products.length} producto(s) registrados</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setAdjustOpen(true)}
              data-testid="inventory-adjust-prices-btn"
              className="bg-white/5 border border-amber-400/30 text-amber-400 font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 hover:bg-amber-400/10 transition"
            >
              <Percent size={18} weight="bold" /> Ajustar precios
            </button>
            <button
              onClick={() => { setSyncResult(null); setSyncOpen(true); }}
              data-testid="inventory-sync-prices-btn"
              className="bg-white/5 border border-cyan-400/30 text-cyan-300 font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 hover:bg-cyan-400/10 transition"
            >
              <ArrowsClockwise size={18} weight="bold" /> Sincronizar precios
            </button>
            <button
              onClick={() => { setImportResult(null); setImportLimit(""); setImportOpen(true); }}
              data-testid="inventory-import-btn"
              className="bg-white/5 border border-turquoise/30 text-turquoise font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 hover:bg-turquoise/10 transition"
            >
              <CloudArrowDown size={18} weight="bold" /> Importar desde criye.ar
            </button>
            <button
              onClick={openNew}
              data-testid="inventory-new-btn"
              className="bg-brand-gradient text-black font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 shadow-cyan-glow hover:opacity-90 transition"
            >
              <Plus size={18} weight="bold" /> Nuevo producto
            </button>
          </div>
        )}
      </header>

      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full bg-[#0F1115] border border-white/10 rounded-md pl-10 pr-4 py-2 text-sm font-mono"
          />
        </div>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-4 font-mono">Producto</th>
                <th className="text-left p-4 font-mono">Categoría</th>
                <th className="text-left p-4 font-mono">Código</th>
                <th className="text-right p-4 font-mono">P. Compra</th>
                <th className="text-right p-4 font-mono">P. Venta</th>
                <th className="text-right p-4 font-mono">Stock</th>
                <th className="text-left p-4 font-mono">Proveedor</th>
                {isAdmin && <th className="p-4"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No hay productos</td></tr>
              ) : filtered.map((p) => (
                <tr key={p.product_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 font-medium">{p.name}</td>
                  <td className="p-4 text-zinc-400">{p.category}</td>
                  <td className="p-4 font-mono text-xs text-zinc-500">{p.barcode || "—"}</td>
                  <td className="p-4 text-right font-mono">$ {fmt(p.price_buy)}</td>
                  <td className="p-4 text-right font-mono text-turquoise">$ {fmt(p.price_sell)}</td>
                  <td className="p-4 text-right">
                    <span className={`font-mono ${p.stock <= p.stock_min ? "text-amber-400" : "text-white"}`}>
                      {p.stock}
                    </span>
                    {p.stock <= p.stock_min && <span className="ml-2 text-[10px] text-amber-400 uppercase font-mono">bajo</span>}
                  </td>
                  <td className="p-4 text-zinc-400 text-xs">{p.supplier_name || "—"}</td>
                  {isAdmin && (
                    <td className="p-4 text-right">
                      <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-turquoise mr-3" data-testid={`edit-${p.product_id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => remove(p.product_id)} className="text-zinc-400 hover:text-red-400" data-testid={`del-${p.product_id}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar" : "Nuevo"} producto</h3>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Nombre *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="product-name-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Categoría</label>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  list="cat-list"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                <datalist id="cat-list">
                  <option value="Repuestos" /><option value="Herramientas" /><option value="Accesorios" /><option value="Otros" />
                </datalist>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Código de barras</label>
                <div className="mt-1.5 flex gap-2">
                  <input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    data-testid="product-barcode-input"
                    className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                  <button type="button" onClick={() => setScannerOpen(true)}
                    className="px-3 bg-white/5 border border-white/10 rounded-md hover:bg-white/10">
                    <Barcode size={18} />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Precio Compra</label>
                <input type="number" step="0.01" value={form.price_buy} onChange={(e) => setForm({ ...form, price_buy: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Precio Venta *</label>
                <input required type="number" step="0.01" value={form.price_sell} onChange={(e) => setForm({ ...form, price_sell: e.target.value })}
                  data-testid="product-price-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Stock</label>
                <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  data-testid="product-stock-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Stock mínimo</label>
                <input type="number" value={form.stock_min} onChange={(e) => setForm({ ...form, stock_min: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Proveedor</label>
                <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                  <option value="">— Sin proveedor —</option>
                  {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Descripción</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows="2" className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-md">Cancelar</button>
                <button type="submit" data-testid="product-save-btn" className="flex-1 bg-brand-gradient text-black font-semibold py-2.5 rounded-md">
                  {editing ? "Actualizar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={(code) => { setForm((f) => ({ ...f, barcode: code })); setScannerOpen(false); }} />

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="import-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <CloudArrowDown size={22} className="text-turquoise" />
                Importar desde criye.ar
              </h3>
              <button onClick={() => !importing && setImportOpen(false)} disabled={importing}>
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {!importResult && !importing && (
                <>
                  <p className="text-sm text-zinc-400 mb-5">
                    Este proceso visita tu tienda Tiendanube en <span className="text-turquoise font-mono">criye.ar</span> y trae todos los productos disponibles.
                    Se llenan los campos: <span className="text-white">nombre, categoría/subcategoría, código (SKU), precio de venta, stock y descripción</span>.
                    Los precios de compra quedan en $0 (los completás manualmente).
                  </p>
                  <div className="bg-turquoise/5 border border-turquoise/20 rounded-md p-3 mb-5">
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      <span className="text-turquoise font-mono uppercase tracking-wider">Importante:</span> los productos que ya tengas cargados (mismo código o mismo nombre) se <span className="text-white">saltean automáticamente</span>, no se duplican ni se sobrescriben.
                    </p>
                  </div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Límite (opcional)</label>
                  <input
                    type="number"
                    value={importLimit}
                    onChange={(e) => setImportLimit(e.target.value)}
                    placeholder="Vacío = todos los productos"
                    data-testid="import-limit-input"
                    className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1 font-mono">Útil para probar con los primeros 5 o 10 antes de hacer la carga completa.</p>

                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setImportOpen(false)} className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-md text-sm">
                      Cancelar
                    </button>
                    <button onClick={runImport} data-testid="import-start-btn"
                      className="flex-1 bg-brand-gradient text-black font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 shadow-cyan-glow">
                      <CloudArrowDown size={16} weight="bold" /> Comenzar importación
                    </button>
                  </div>
                </>
              )}

              {importing && (
                <div className="py-8 text-center" data-testid="import-progress">
                  <div className="w-12 h-12 border-2 border-turquoise border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-white font-medium">Importando productos...</p>
                  {importProgress.total > 0 ? (
                    <>
                      <p className="text-xs text-zinc-400 mt-3 font-mono">
                        {importProgress.done} / {importProgress.total} productos
                      </p>
                      <div className="w-full max-w-xs mx-auto mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-gradient transition-all"
                          style={{ width: `${Math.min(100, Math.round((importProgress.done / importProgress.total) * 100))}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-2 font-mono">
                        Se procesa en tandas para evitar timeouts del proveedor.
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-zinc-500 mt-2 font-mono">Leyendo sitemap de criye.ar...</p>
                  )}
                </div>
              )}

              {importResult && !importing && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="glass-card rounded-md p-4 text-center border border-turquoise/30">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Nuevos</p>
                      <p className="text-3xl font-mono text-turquoise tabular-nums mt-1">{importResult.created}</p>
                    </div>
                    <div className="glass-card rounded-md p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Salteados</p>
                      <p className="text-3xl font-mono text-zinc-400 tabular-nums mt-1">{importResult.skipped}</p>
                    </div>
                    <div className="glass-card rounded-md p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Errores</p>
                      <p className="text-3xl font-mono text-red-400 tabular-nums mt-1">{importResult.errors}</p>
                    </div>
                  </div>

                  <div className="border-t border-white/5 pt-4">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-3">
                      Detalle ({importResult.details?.length || 0} items)
                    </p>
                    <div className="max-h-72 overflow-y-auto space-y-1">
                      {importResult.details?.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-white/[0.02] text-xs">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                              d.status === "created" ? "bg-turquoise/10 text-turquoise" :
                              d.status === "skipped" ? "bg-white/5 text-zinc-400" :
                              "bg-red-500/10 text-red-400"
                            }`}>
                              {d.status === "created" ? "NUEVO" : d.status === "skipped" ? "EXISTE" : "ERROR"}
                            </span>
                            <span className="truncate">{d.name || d.reason || d.url}</span>
                          </div>
                          {d.status === "created" && (
                            <span className="font-mono text-zinc-500 shrink-0 ml-2">${d.price}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setImportOpen(false)} className="w-full bg-brand-gradient text-black font-semibold py-2.5 rounded-md mt-5">
                    Cerrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {syncOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="sync-prices-modal">
          <div className="glass-card rounded-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto border border-cyan-400/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <ArrowsClockwise size={20} weight="duotone" className="text-cyan-300" /> Sincronizar precios desde criye.ar
              </h3>
              <button onClick={() => !syncing && setSyncOpen(false)} data-testid="sync-close"><X size={20} /></button>
            </div>

            {!syncResult && !syncing && (
              <>
                <p className="text-sm text-zinc-400 mb-3">
                  Lee todos los productos de <span className="text-cyan-300">criye.ar</span> y actualiza el <strong>precio de venta</strong> de los que ya tenés en tu inventario (match por código de barras o nombre).
                </p>
                <ul className="text-xs text-zinc-500 font-mono list-disc list-inside mb-5 space-y-0.5">
                  <li>No crea productos nuevos (usá "Importar" para eso)</li>
                  <li>No toca stock, costo ni descripción — solo precio de venta</li>
                  <li>Tarda ~1 minuto para ~1000 productos</li>
                </ul>
                <div className="flex gap-3">
                  <button
                    onClick={() => runSyncPrices({ dryRun: true })}
                    data-testid="sync-dry-run-btn"
                    className="flex-1 py-2.5 rounded-md border border-white/15 text-sm font-medium hover:bg-white/5"
                  >
                    Vista previa (sin cambios)
                  </button>
                  <button
                    onClick={() => runSyncPrices()}
                    data-testid="sync-apply-btn"
                    className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 transition"
                  >
                    Sincronizar ahora
                  </button>
                </div>
              </>
            )}

            {syncing && (
              <div className="py-8 text-center" data-testid="sync-progress">
                <div className="w-12 h-12 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white font-medium">Sincronizando precios...</p>
                {syncProgress.total > 0 && (
                  <>
                    <p className="text-xs text-zinc-400 mt-3 font-mono">{syncProgress.done} / {syncProgress.total}</p>
                    <div className="w-full max-w-xs mx-auto mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-300 transition-all" style={{ width: `${Math.min(100, Math.round((syncProgress.done / syncProgress.total) * 100))}%` }} />
                    </div>
                  </>
                )}
              </div>
            )}

            {syncResult && !syncing && (
              <div data-testid="sync-result">
                <div className="grid grid-cols-4 gap-2 mb-4 text-center text-xs font-mono">
                  <div className="p-2 bg-turquoise/5 border border-turquoise/20 rounded">
                    <div className="text-turquoise text-lg">{syncResult.updated}</div>
                    <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Actualizados</div>
                  </div>
                  <div className="p-2 bg-white/5 border border-white/10 rounded">
                    <div className="text-zinc-300 text-lg">{syncResult.unchanged}</div>
                    <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Sin cambio</div>
                  </div>
                  <div className="p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                    <div className="text-amber-400 text-lg">{syncResult.notFound}</div>
                    <div className="text-zinc-500 uppercase tracking-wider text-[10px]">No en DB</div>
                  </div>
                  <div className="p-2 bg-red-500/5 border border-red-500/20 rounded">
                    <div className="text-red-400 text-lg">{syncResult.errors}</div>
                    <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Errores</div>
                  </div>
                </div>
                {syncResult.dryRun && (
                  <p className="text-[11px] text-cyan-300 font-mono mb-3 text-center">
                    Vista previa — no se aplicaron cambios aún
                  </p>
                )}
                {syncResult.updated > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">
                      Cambios ({syncResult.updated})
                    </p>
                    <div className="max-h-56 overflow-y-auto space-y-1 text-xs font-mono">
                      {syncResult.details.filter((d) => d.status === "updated").slice(0, 50).map((d, i) => (
                        <div key={i} className="flex justify-between items-center gap-2 p-1.5 bg-white/[0.02] rounded">
                          <span className="truncate flex-1 text-zinc-300">{d.name}</span>
                          <span className="text-zinc-500">${d.old_price}</span>
                          <span className="text-cyan-300">→</span>
                          <span className="text-turquoise font-semibold">${d.new_price}</span>
                          <span className={`text-[10px] w-14 text-right ${d.diff_pct > 0 ? "text-amber-400" : "text-cyan-300"}`}>
                            {d.diff_pct > 0 ? "+" : ""}{d.diff_pct}%
                          </span>
                        </div>
                      ))}
                      {syncResult.updated > 50 && (
                        <p className="text-[10px] text-zinc-600 text-center pt-1">...y {syncResult.updated - 50} más</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 mt-4">
                  {syncResult.dryRun ? (
                    <>
                      <button onClick={() => setSyncResult(null)} className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">
                        Volver
                      </button>
                      <button
                        onClick={() => runSyncPrices()}
                        data-testid="sync-apply-after-preview"
                        className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90"
                      >
                        Aplicar cambios
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setSyncOpen(false)} data-testid="sync-done-btn" className="w-full py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90">
                      Listo
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {adjustOpen && (
        <AdjustPricesModal
          onClose={() => setAdjustOpen(false)}
          products={products}
          adjust={adjust}
          setAdjust={setAdjust}
          loading={adjustLoading}
          onRun={async () => {
            setAdjustLoading(true);
            try {
              const body = {
                percentage: parseFloat(adjust.percentage) || 0,
                direction: adjust.direction,
                target: adjust.target,
                round_to: parseInt(adjust.round_to) || 0,
              };
              if (adjust.category && adjust.category !== "Todos") body.category = adjust.category;
              const { data } = await api.post("/products/adjust-prices", body);
              toast.success(`${data.updated} productos actualizados (factor ×${data.factor})`);
              setAdjustOpen(false);
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Error al ajustar precios");
            } finally {
              setAdjustLoading(false);
            }
          }}
        />
      )}
    </div>
  );
}

function AdjustPricesModal({ onClose, products, adjust, setAdjust, loading, onRun }) {
  const cats = ["Todos", ...Array.from(new Set(products.map((p) => p.category))).sort()];
  const affected = products.filter((p) => adjust.category === "Todos" || p.category === adjust.category);
  const factor = adjust.direction === "increase"
    ? 1 + (parseFloat(adjust.percentage) || 0) / 100
    : 1 - (parseFloat(adjust.percentage) || 0) / 100;
  const sample = affected.slice(0, 3);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="adjust-prices-modal">
      <div className="glass-card rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto border border-amber-400/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-amber-400/10">
            <Percent size={20} weight="duotone" className="text-amber-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-mono">Ajuste masivo</p>
            <h3 className="text-lg font-heading font-bold">Modificar precios</h3>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setAdjust({ ...adjust, direction: "increase" })}
              data-testid="adjust-direction-up"
              className={`py-2 rounded-md border font-mono text-xs uppercase tracking-wider transition ${
                adjust.direction === "increase" ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400"
              }`}
            >
              ↑ Aumentar
            </button>
            <button
              onClick={() => setAdjust({ ...adjust, direction: "decrease" })}
              data-testid="adjust-direction-down"
              className={`py-2 rounded-md border font-mono text-xs uppercase tracking-wider transition ${
                adjust.direction === "decrease" ? "border-red-400 bg-red-400/10 text-red-400" : "border-white/10 text-zinc-400"
              }`}
            >
              ↓ Disminuir
            </button>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Porcentaje (%)</label>
            <input
              type="number"
              min="0.1"
              max="500"
              step="0.1"
              value={adjust.percentage}
              onChange={(e) => setAdjust({ ...adjust, percentage: e.target.value })}
              data-testid="adjust-percentage-input"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 focus:border-amber-400 outline-none font-mono text-lg"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Aplicar a</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "sale", l: "Venta" },
                { v: "cost", l: "Costo" },
                { v: "both", l: "Ambos" },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => setAdjust({ ...adjust, target: v })}
                  data-testid={`adjust-target-${v}`}
                  className={`py-2 rounded-md border text-xs font-mono uppercase tracking-wider ${
                    adjust.target === v ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Categoría</label>
            <select
              value={adjust.category}
              onChange={(e) => setAdjust({ ...adjust, category: e.target.value })}
              data-testid="adjust-category-select"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 focus:border-amber-400 outline-none text-sm"
            >
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Redondear a</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { v: 0, l: "No" },
                { v: 1, l: "$1" },
                { v: 10, l: "$10" },
                { v: 100, l: "$100" },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => setAdjust({ ...adjust, round_to: v })}
                  data-testid={`adjust-round-${v}`}
                  className={`py-2 rounded-md border text-xs font-mono ${
                    parseInt(adjust.round_to) === v ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-amber-400/5 border border-amber-400/20 rounded-md p-3 mb-4">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 font-mono mb-2">
            Va a afectar {affected.length} producto(s) — factor ×{factor.toFixed(3)}
          </p>
          <div className="space-y-1 text-xs font-mono">
            {sample.map((p) => (
              <div key={p.product_id} className="flex justify-between">
                <span className="truncate flex-1 text-zinc-400">{p.name.substring(0, 30)}</span>
                <span className="text-zinc-500">${p.price_sell?.toFixed(0)}</span>
                <span className="mx-2 text-amber-400">→</span>
                <span className="text-turquoise">${(p.price_sell * factor).toFixed(0)}</span>
              </div>
            ))}
            {affected.length > 3 && (
              <p className="text-zinc-600 text-[10px]">... y {affected.length - 3} más</p>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} disabled={loading} className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">
            Cancelar
          </button>
          <button
            onClick={onRun}
            disabled={loading || affected.length === 0}
            data-testid="adjust-run-btn"
            className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-amber-400 hover:bg-amber-300 disabled:opacity-40 transition"
          >
            {loading ? "Aplicando..." : "Aplicar ajuste"}
          </button>
        </div>
      </div>
    </div>
  );
}
